import {
  DownloadSimpleIcon,
  GiftIcon,
  MagicWandIcon,
  PlusIcon,
  StarIcon,
  WalletIcon,
  ArrowsClockwise,
} from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { Link } from "react-router";
import { describeCreditExpiry } from "@/client/services/credit/expiry";
import { useAccountCurrency } from "@/client/services/currency/currency";
import { useDialog } from "@/client/stores/dialog";
import { useAccountOverview } from "../hooks/use-account-overview";
import { USER_SPACE_PATHS } from "../config/navigation";

export const DashboardMetrics = () => {
  const { overview, loading, refreshing, refetch } = useAccountOverview();
  const { format } = useAccountCurrency();
  const { open: openOffers } = useDialog("forfait");

  const handleOpenOffers = () => {
    openOffers("create", { id: "forfait" });
  };

  const metrics = [
    {
      label: "Crédits IA",
      value: overview.aiCredits,
      icon: MagicWandIcon,
      suffix: "restants",
      path: USER_SPACE_PATHS.aiCredits,
      colSpan: "col-span-1",
      iconClass: "text-violet-700 bg-violet-50 border-violet-100",
    },
    {
      label: "Téléchargements",
      value: overview.downloadCredits,
      note:
        overview.downloadCreditsExpiring.length > 0
          ? `Dont ${describeCreditExpiry(overview.downloadCreditsExpiring)}`
          : undefined,
      icon: DownloadSimpleIcon,
      suffix: "restants",
      path: USER_SPACE_PATHS.resumes, // Actually opens offers on desktop, but links to resumes
      colSpan: "col-span-1",
      iconClass: "text-indigo-700 bg-indigo-50 border-indigo-100",
      onClick: (e: React.MouseEvent) => {
        e.preventDefault();
        handleOpenOffers();
      },
    },
    {
      label: "Fidélité",
      value: overview.loyaltyPoints,
      icon: StarIcon,
      suffix: "pts",
      path: USER_SPACE_PATHS.fidelity,
      colSpan: "col-span-1",
      iconClass: "text-amber-600 bg-amber-50 border-amber-100",
    },
    {
      label: "Récompenses",
      value: overview.totalRewardsFcfa,
      icon: GiftIcon,
      suffix: "",
      money: true,
      path: USER_SPACE_PATHS.referral,
      colSpan: "col-span-1",
      iconClass: "text-emerald-600 bg-emerald-50 border-emerald-100",
    },
  ];

  return (
    <section className="mb-5 mt-2 lg:hidden">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Offre {overview.currentPlan.name}
          </span>
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={refreshing || loading}
            aria-label="Actualiser"
            className={cn(
              "-ml-1 rounded-full p-1 text-slate-400 transition-colors hover:text-indigo-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
              (refreshing || loading) && "animate-spin text-indigo-400",
            )}
          >
            <ArrowsClockwise size={14} weight="bold" />
          </button>
        </div>

        <button
          type="button"
          onClick={handleOpenOffers}
          className="flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold text-slate-700 transition-colors hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          <PlusIcon size={10} weight="bold" />
          Voir les offres
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {metrics.map((metric, index) => {
          const IconComponent = metric.icon;
          return (
            <Link
              key={index}
              to={metric.path}
              onClick={metric.onClick}
              className={cn(
                "flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-[0_2px_8px_rgba(15,23,42,0.04)]",
                "transition-colors hover:border-slate-300 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
                metric.colSpan,
              )}
            >
              <span
                className={cn(
                  "grid size-8 shrink-0 place-items-center rounded-lg border",
                  metric.iconClass,
                )}
              >
                <IconComponent size={16} weight="duotone" />
              </span>
              <div className="flex min-w-0 flex-col">
                <span className="mb-1 truncate text-[10px] font-medium leading-none text-slate-500">
                  {metric.label}
                </span>
                <span className="truncate text-[13px] font-bold leading-none text-indigo-950">
                  {loading ? (
                    <span className="inline-block h-3.5 w-10 animate-pulse rounded bg-slate-200" />
                  ) : "money" in metric ? (
                    format(metric.value)
                  ) : (
                    `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(metric.value)} ${metric.suffix}`
                  )}
                </span>
                {!loading && "note" in metric && metric.note && (
                  <span className="mt-1 text-[10px] leading-tight text-slate-500">
                    {metric.note}
                  </span>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};
